import type { UIViewInjectedProps } from '@uirouter/react';
import { useState } from 'react';

import { getTranslatedString } from '@/utils';
import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { Upload } from '@/components/upload';
import { Button, Col, Form, message, notification, Select } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import { lotStore } from '@/stores';
import { showError } from '@/utils/common';

const TEMPLATE_OPTIONS = [{ value: 'Cyrian - San Pedro', label: 'Cyrian - San Pedro' }];

// Mirrors Cyrian-Backend IMPORT_REFERENCE_NOT_FOUND (errors/codes.ts): import references master data that doesn't exist yet.
const IMPORT_REFERENCE_NOT_FOUND = 12052;
const FACTORY_NOT_FOUND_NOTIF_KEY = 'lots-import-factory-not-found';

export const LinkLotsWaybills = ({ transition }: UIViewInjectedProps) => {
  const [templateKey, setTemplateKey] = useState<string>(TEMPLATE_OPTIONS[0].value);

  const showFactoryNotFoundWarning = () => {
    notification.warning({
      key: FACTORY_NOT_FOUND_NOTIF_KEY,
      message: getTranslatedString('lots.link-lots-waybills.factory-not-found-title'),
      description: getTranslatedString('lots.link-lots-waybills.factory-not-found-desc', { 0: templateKey }),
      duration: 0,
      btn: (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            notification.close(FACTORY_NOT_FOUND_NOTIF_KEY);
            transition.router.stateService.go('base-layout.addresses.create');
          }}
        >
          {getTranslatedString('lots.link-lots-waybills.factory-not-found-action')}
        </Button>
      ),
    });
  };

  const request = async (fileList: RcFile[]) => {
    if (fileList.length !== 1) {
      message.error('Supports only one file');
      throw new Error('Invalid number of files');
    }

    const validExtensions = ['.xls', '.xlsx'];
    const invalidFiles = fileList.filter(
      (file: RcFile) => !validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)),
    );
    if (invalidFiles.length > 0) {
      message.error('You can upload only Excel files');
      throw new Error('Invalid file extension');
    }

    const formData = new FormData();
    const file: RcFile = fileList[0];
    formData.append('file', file);
    formData.append('templateKey', templateKey);

    try {
      await api.lots.linkLotsWithWaybills(formData).source;
    } catch (err: any) {
      const code = err?.response?.data?.error?.code ?? err?.response?.data?.code;
      if (code === IMPORT_REFERENCE_NOT_FOUND) {
        showFactoryNotFoundWarning();
      } else {
        showError(err);
      }
      // Rethrow so Upload keeps the drawer open and skips the success message; suppressBuiltInError mutes its showError.
      throw err;
    }

    await lotStore.refresh();
    transition.router.stateService.go('base-layout.lots');
  };

  const onClose = () => {
    transition.router.stateService.go('base-layout.lots');
  };

  return (
    <Drawer onClose={onClose} visible={true} title={getTranslatedString('lots.link-lots-waybills')}>
      <Col>
        <Form layout="vertical" style={{ marginBottom: 16 }}>
          <Form.Item label={getTranslatedString('lots.processingReportTemplate')} required>
            <Select
              options={TEMPLATE_OPTIONS}
              value={templateKey}
              onChange={(v) => setTemplateKey(v)}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
        <Upload
          request={request}
          multiple={false}
          accept=".xls,.xlsx"
          suppressBuiltInError={true}
          title="Click or drag the processing report Excel here"
          description="Single .xls or .xlsx file."
        />
      </Col>
    </Drawer>
  );
};
